import { useTranslation } from 'react-i18next';
import { Details, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import {
  ChatExclamationmarkIcon,
  CheckmarkCircleIcon,
  ExclamationmarkTriangleIcon,
  HandshakeIcon,
  HeartIcon,
  PersonGroupIcon,
  ShieldLockIcon,
  XMarkIcon,
} from '@navikt/aksel-icons';
import type { TeamHealthMetrics } from '../../../../api/queries';
import { getHealthCategoryKey, type HealthCategoryKey } from '../../../../utils/healthCategory';
import { agreementOf } from '../../../../domain/psychological-safety/team-result';
import {
  agreementTones,
  formatScore,
  getScoreLevel,
  levelTones,
} from '../../../../components/AnalysisLayout/analysis.utils';
import { ActionPlanSummaryCard } from '../ActionPlanSummaryCard';
import classes from './TeamResults.module.css';

const icons: Record<HealthCategoryKey, typeof HeartIcon> = {
  safetyAndOpenness: ChatExclamationmarkIcon,
  recognitionAndAppreciation: HeartIcon,
  trustAndSupport: HandshakeIcon,
  inclusionAndRespect: PersonGroupIcon,
};

const agreementIcons = {
  success: CheckmarkCircleIcon,
  warning: ExclamationmarkTriangleIcon,
  danger: XMarkIcon,
};

const HELP_STEPS = ['look', 'think', 'answer'] as const;

/** The score alone, as on the team dashboard; agreement has its own line and colour */
const toneForMetric = (metric: TeamHealthMetrics) => levelTones[getScoreLevel(metric.score)];

const AreaCard = ({ metric }: { metric: TeamHealthMetrics }) => {
  const { t } = useTranslation();
  const key = getHealthCategoryKey(metric.category);
  const Icon = key ? icons[key] : HeartIcon;
  const name = key ? t(`teamHealthCategories.${key}.title`) : metric.category;
  const description = key ? t(`teamHealthCategories.${key}.description`) : metric.description;
  const hasScore = metric.score > 0;
  const agreement = hasScore ? agreementOf(metric.standardDeviation) : null;
  const agreementTone = agreement ? agreementTones[agreement] : null;
  const AgreementIcon = agreementTone ? agreementIcons[agreementTone] : null;
  // Skalaen går fra 1 til 5
  const width = `${Math.min(100, Math.max(0, ((metric.score - 1) / 4) * 100))}%`;

  return (
    <li className={classes.area}>
      <div className={classes.areaBody}>
        <div className={classes.areaTop}>
          <div className={classes.areaTitle}>
            <span aria-hidden="true" className={classes.areaIcon}>
              <Icon />
            </span>
            <Heading level={3} data-size="xs" className={classes.areaName}>
              {name}
            </Heading>
          </div>
          {hasScore && (
            <p className={classes.score}>
              <span className={classes.scoreValue}>{formatScore(metric.score, 2)}</span>
              <span className={classes.subtle}>{t('publicResults.of5')}</span>
            </p>
          )}
        </div>
        {hasScore ? (
          <span aria-hidden="true" className={classes.track}>
            <span className={classes.fill} data-tone={toneForMetric(metric)} style={{ width }} />
          </span>
        ) : (
          <Paragraph data-size="sm">
            <strong>{t('dashboard.teamHealth.noData')}.</strong> {t('dashboard.teamHealth.noDataDescription')}
          </Paragraph>
        )}
        <Paragraph data-size="sm" className={classes.subtle}>
          {description}
        </Paragraph>
      </div>
      {agreement && AgreementIcon && (
        <div className={classes.agreement} data-tone={agreementTone!}>
          <AgreementIcon aria-hidden className={classes.agreementIcon} />
          <Paragraph data-size="sm">
            <strong>{t(`teamResult.agreement.${agreement}`)}.</strong> {t(`teamResult.agreementForTeam.${agreement}`)}
          </Paragraph>
        </div>
      )}
    </li>
  );
};

type TeamResultsProps = {
  surveyTitle: string;
  metrics: TeamHealthMetrics[];
  hasSubmittedActionPlan: boolean;
  surveyUuid: string;
  onViewFullReport: () => void;
};

/** Resultatene teamet ser etter en måling: de fire områdene, hvordan de leses og hva hver kan bidra med */
export const TeamResults = ({
  surveyTitle,
  metrics,
  hasSubmittedActionPlan,
  surveyUuid,
  onViewFullReport,
}: TeamResultsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.content}>
        <div className={classes.intro}>
          <span className={classes.eyebrow}>{t('publicResults.eyebrow')}</span>
          <Heading level={1} data-size="2xl" className={classes.title}>
            {surveyTitle}
          </Heading>
          <Paragraph className={classes.lead}>{t('publicResults.intro')}</Paragraph>
          <div>
            <Tag data-size="sm" data-color="neutral" className={classes.anonymous}>
              <ShieldLockIcon aria-hidden />
              {t('publicResults.anonymous')}
            </Tag>
          </div>
        </div>

        <section aria-labelledby="how-to-read-heading" className={classes.howToRead}>
          <Heading id="how-to-read-heading" level={2} data-size="2xs">
            {t('publicResults.howToRead.title')}
          </Heading>
          <dl className={classes.legend}>
            <div>
              <dt>{t('publicResults.howToRead.score')}</dt>
              <dd>{t('publicResults.howToRead.scoreDescription')}</dd>
            </div>
            <div>
              <dt>{t('publicResults.howToRead.agreement')}</dt>
              <dd>{t('publicResults.howToRead.agreementDescription')}</dd>
            </div>
          </dl>
        </section>

        <section aria-labelledby="areas-heading" className={classes.section}>
          <Heading id="areas-heading" level={2} data-size="md">
            {t('publicResults.areasTitle')}
          </Heading>
          <ul className={classes.areas}>
            {metrics.map((metric) => (
              <AreaCard key={metric.id} metric={metric} />
            ))}
          </ul>
        </section>

        {hasSubmittedActionPlan && (
          <ActionPlanSummaryCard surveyUuid={surveyUuid} onViewFullReport={onViewFullReport} />
        )}

        <section aria-labelledby="contribute-heading" className={classes.contribute}>
          <div className={classes.contributeIntro}>
            <span aria-hidden="true" className={classes.contributeIcon}>
              <ChatExclamationmarkIcon />
            </span>
            <Heading id="contribute-heading" level={2} data-size="md">
              {t('publicResults.contribute.title')}
            </Heading>
            <Paragraph className={classes.subtle}>{t('publicResults.contribute.intro')}</Paragraph>
          </div>
          <ol className={classes.steps}>
            {HELP_STEPS.map((step, index) => (
              <li key={step} className={classes.step}>
                <span aria-hidden="true" className={classes.stepNumber}>
                  {index + 1}
                </span>
                <Paragraph>{t(`publicResults.contribute.steps.${step}`)}</Paragraph>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="faq-heading" className={classes.faq}>
          <Heading id="faq-heading" level={2} data-size="md">
            {t('publicResults.faqTitle')}
          </Heading>
          <Details>
            <Details.Summary>{t('publicExplanationSection.howToRead')}</Details.Summary>
            <Details.Content className={classes.faqContent}>
              <Paragraph>{t('publicExplanationSection.howToReadContent.intro')}</Paragraph>
              <Paragraph>{t('publicExplanationSection.howToReadContent.scoreExplanation')}</Paragraph>
              <Paragraph>{t('publicExplanationSection.howToReadContent.disagreementExplanation')}</Paragraph>
            </Details.Content>
          </Details>
        </section>
      </div>
    </>
  );
};
